import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";

const COLOR_PRESETS = [
  "#3b82f6", "#22c55e", "#ef4444", "#f59e0b", "#8b5cf6", 
  "#ec4899", "#06b6d4", "#f97316", "#6366f1", "#14b8a6"
];

// "Nueva Cita" / "Modificar Cita" form dialog of the Planning. The Planning keeps the
// state (appt) and the save logic (onSubmit); this is only the form.
const AppointmentFormDialog = ({ open, onOpenChange, editing, appt, setAppt, currentUser, dossiers, submitting, onSubmit, onCancel }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto" data-testid="create-appointment-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Modificar Cita" : "Nueva Cita"}</DialogTitle>
          <DialogDescription>{editing ? "Modificar los datos de la cita" : "Crear una nueva cita en el planning"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nombre / Apellido *</Label>
            <Input value={appt.client_name} onChange={e => setAppt(p => ({ ...p, client_name: e.target.value }))} placeholder="Nombre y apellido" data-testid="appt-client-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fecha *</Label>
              <Input type="date" value={appt.date} onChange={e => setAppt(p => ({ ...p, date: e.target.value }))} data-testid="appt-date" />
            </div>
            {currentUser?.role !== "commercial" && (
            <div>
              <Label>Expediente (opcional)</Label>
              <Select value={appt.dossier_id} onValueChange={v => {
                const dos = dossiers.find(d => d.id === v);
                if (dos) {
                  const donnees = dos.donnees || {};
                  const identite = donnees.identite || {};
                  const batiment = donnees.batiment || {};
                  setAppt(p => ({
                    ...p, dossier_id: v,
                    client_name: identite.nom_proprietaire || p.client_name,
                    address: `${identite.adresse_complete || ''}, ${identite.ville || ''}`,
                    phone: identite.telephone || p.phone,
                    email: identite.email || p.email,
                    ref_cadastre: batiment.ref_catastrale || p.ref_cadastre,
                    type_logement: batiment.type_logement || p.type_logement,
                    superficie: String(batiment.superficie_habitable || p.superficie),
                    date_pose: dos.date_installation || p.date_pose,
                  }));
                } else { setAppt(p => ({ ...p, dossier_id: v })); }
              }}>
                <SelectTrigger data-testid="appt-dossier"><SelectValue placeholder="Vincular expediente" /></SelectTrigger>
                <SelectContent>
                  {dossiers.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.donnees?.identite?.nom_proprietaire || d.reference || d.id.slice(0, 8)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}
          </div>
            <div>
              <Label>Hora *</Label>
              <Input type="time" min="06:00" max="21:00" value={appt.start_time} onChange={e => setAppt(p => ({ ...p, start_time: e.target.value, end_time: `${String(parseInt(e.target.value.split(":")[0], 10) + 1).padStart(2, '0')}:${e.target.value.split(":")[1] || '00'}` }))} data-testid="appt-start-time" />
            </div>
          <div>
            <Label>Dirección</Label>
            <Input value={appt.address} onChange={e => setAppt(p => ({ ...p, address: e.target.value }))} placeholder="Calle, número" data-testid="appt-address" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Ciudad</Label>
              <Input value={appt.city || ""} onChange={e => setAppt(p => ({ ...p, city: e.target.value }))} placeholder="Ciudad" data-testid="appt-city" />
            </div>
            <div>
              <Label>Código Postal</Label>
              <Input value={appt.postal_code || ""} onChange={e => setAppt(p => ({ ...p, postal_code: e.target.value }))} placeholder="CP" data-testid="appt-postal-code" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Teléfono{!editing && currentUser?.role === "commercial" && " *"}</Label>
              <Input value={appt.phone} onChange={e => setAppt(p => ({ ...p, phone: e.target.value }))} data-testid="appt-phone" />
            </div>
            <div>
              <Label>Correo electrónico{!editing && currentUser?.role === "commercial" && " *"}</Label>
              <Input value={appt.email} onChange={e => setAppt(p => ({ ...p, email: e.target.value }))} data-testid="appt-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Referencia Catastral{!editing && currentUser?.role === "commercial" && " *"}</Label>
              <Input value={appt.ref_cadastre} onChange={e => setAppt(p => ({ ...p, ref_cadastre: e.target.value }))} data-testid="appt-ref-cadastre" />
            </div>
            <div>
              <Label>Venta realizada por</Label>
              <Select value={appt.vendeur || ""} onValueChange={v => {
                const eqMap = { "Equipe 1": "equipe1", "Equipe 2": "equipe2", "Equipe 3": "equipe3" };
                setAppt(p => ({ ...p, vendeur: v, ...(eqMap[v] ? { equipe: eqMap[v] } : {}) }));
              }}>
                <SelectTrigger data-testid="appt-vendeur"><SelectValue placeholder="Elegir" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Andrea">Andrea</SelectItem>
                  <SelectItem value="Vanessa">Vanessa</SelectItem>
                  <SelectItem value="Anna">Anna</SelectItem>
                  <SelectItem value="Meru">Meru</SelectItem>
                  <SelectItem value="Silvia">Silvia</SelectItem>
                  <SelectItem value="Equipe 1">Equipe 1</SelectItem>
                  <SelectItem value="Equipe 2">Equipe 2</SelectItem>
                  <SelectItem value="Equipe 3">Equipe 3</SelectItem>
                  <SelectItem value="Parrainage">Parrainage</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fuente</Label>
              <Select value={appt.source || ""} onValueChange={v => setAppt(p => ({ ...p, source: v }))}>
                <SelectTrigger data-testid="appt-source"><SelectValue placeholder="Elegir" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Google ADS">Google ADS</SelectItem>
                  <SelectItem value="Facebook & Instagram">Facebook & Instagram</SelectItem>
                  <SelectItem value="Tik Tok ADS">Tik Tok ADS</SelectItem>
                  <SelectItem value="Parrainage">Parrainage</SelectItem>
                  <SelectItem value="Appel entrant">Appel entrant</SelectItem>
                  <SelectItem value="Terrain (porte à porte)">Terrain (porte à porte)</SelectItem>
                  <SelectItem value="Autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={appt.status || "Instalación programada"} onValueChange={v => setAppt(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="appt-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Instalación programada">Instalación programada</SelectItem>
                  <SelectItem value="Instalado">Instalado</SelectItem>
                  <SelectItem value="Validado">Validado</SelectItem>
                  <SelectItem value="Anulado">Anulado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo de vivienda</Label>
              <Select value={appt.type_logement || ""} onValueChange={v => setAppt(p => ({ ...p, type_logement: v }))}>
                <SelectTrigger data-testid="appt-type-logement"><SelectValue placeholder="Elegir" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="apartamento">Apartamento</SelectItem>
                  <SelectItem value="casa">Casa</SelectItem>
                  <SelectItem value="local">Local</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Equipo técnico</Label>
              <Select value={appt.equipe || "equipe1"} onValueChange={v => setAppt(p => ({ ...p, equipe: v }))}>
                <SelectTrigger data-testid="appt-equipe">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equipe1">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: "#8b5cf6" }} />
                      Equipo 1
                    </span>
                  </SelectItem>
                  <SelectItem value="equipe2">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: "#f97316" }} />
                      Equipo 2
                    </span>
                  </SelectItem>
                  <SelectItem value="equipe3">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: "#475569" }} />
                      Equipo 3
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>m² instalación</Label>
            <Input value={appt.m2_instalacion} onChange={e => setAppt(p => ({ ...p, m2_instalacion: e.target.value }))} placeholder="m² instalados" data-testid="appt-m2-instalacion" />
          </div>
          <div>
            <Label>Color en el planning</Label>
            <div className="flex items-center gap-2 mt-1">
              {COLOR_PRESETS.map(c => (
                <button key={c} onClick={() => setAppt(p => ({ ...p, color: c }))}
                  className={`w-6 h-6 rounded-full border-2 transition-transform ${appt.color === c ? 'border-slate-800 scale-110' : 'border-transparent hover:scale-105'}`}
                  style={{ backgroundColor: c }}
                  data-testid={`color-${c}`} />
              ))}
              <input type="color" value={appt.color} onChange={e => setAppt(p => ({ ...p, color: e.target.value }))}
                className="w-6 h-6 rounded cursor-pointer border-0 p-0" data-testid="appt-color-picker" />
            </div>
          </div>
          <div>
            <Label>Notas</Label>
            <Textarea value={appt.notes} onChange={e => setAppt(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="appt-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button>
          <Button onClick={onSubmit} disabled={submitting} data-testid="save-appointment">
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
            ) : (
              editing ? "Actualizar" : "Guardar"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentFormDialog;
